import React from 'react'
import { connect } from 'react-redux'
import {
  EntityTypeEnum,
  OrganisationType,
  ReferenceDataManagementEvent,
  TradingEntityMeta,
  TradingRelationshipDetail,
  WorkflowActions,
  WorkflowDetail
} from 'api'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { Locale, Translation } from 'locale'
import { TradingEntityRelationshipsDialog } from './TradingEntityRelationshipsDialog'
import { NEW_ITEM_PREFIX } from 'utils'

export interface TradingEntityRelationshipsActionsButtonProps
  extends ModalConsumerProps,
    ActionsButtonProps<WorkflowDetail<TradingRelationshipDetail>> {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  context: {
    workflowId: string
    tradingEntityId: string
    organisationId: string
    tradingEntityMetas: TradingEntityMeta[]
  }
}

const mapStateToProps = null

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog
}

const canEditDetails = (item: WorkflowDetail<TradingRelationshipDetail>) =>
  item.availableEvents?.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)

const TradingEntityRelationshipsActionsButtonComponent: React.FunctionComponent<TradingEntityRelationshipsActionsButtonProps> = props => {
  const {
    items,
    onShowDialog,
    showModal,
    context: { workflowId, tradingEntityId, organisationId, tradingEntityMetas }
  } = props

  const editItems = items.filter(item => canEditDetails(item))

  const handleShowItemDialog = (tradingRelationshipDetails: TradingRelationshipDetail[]) => {
    const {
      tradingRelationship: { id },
      organisationType
    } = tradingRelationshipDetails[0]

    showModal(
      <TradingEntityRelationshipsDialog
        tradingEntityId={tradingEntityId}
        organisationId={organisationId}
        entityId={id}
        workflowId={workflowId}
        organisationType={organisationType}
      />
    )
  }

  const handleDelete = (tradingRelationshipDetails: TradingRelationshipDetail[]) => {
    const { organisationType, buyTradingEntity, sellTradingEntity } = tradingRelationshipDetails[0]

    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_DELETE_SINGLE_TRADING_RELATIONSHIP)

    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_TRADING_RELATIONSHIP, {
      tradingEntityName: organisationType === OrganisationType.BUY_SIDE ? sellTradingEntity?.publishedName : buyTradingEntity?.publishedName
    })

    const relatedTradingEntityMetaIds = tradingEntityMetas
      .filter(
        tradingEntityMeta =>
          (tradingEntityMeta.ownerTradingEntityId === buyTradingEntity?.id &&
            tradingEntityMeta.subjectTradingEntityId === sellTradingEntity?.id) ||
          (tradingEntityMeta.ownerTradingEntityId === sellTradingEntity?.id &&
            tradingEntityMeta.subjectTradingEntityId === buyTradingEntity?.id)
      )
      .map(tradingEntityMeta => tradingEntityMeta.id)
      .filter(tradingEntityMetaId => !tradingEntityMetaId.startsWith(NEW_ITEM_PREFIX))

    const deleteActions = [
      WorkflowActions.createEntityDeleteReferenceData(
        [
          {
            entityType: EntityTypeEnum.TRADING_RELATIONSHIP,
            entityId: tradingRelationshipDetails[0].tradingRelationship.id
          },
          ...relatedTradingEntityMetaIds.map(tradingEntityMetaId => ({
            entityType: EntityTypeEnum.TRADING_ENTITY_META,
            entityId: tradingEntityMetaId
          }))
        ],
        workflowId
      )
    ]

    onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: deleteActions
      }
    })
  }

  const actions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_ACTION_EDIT_DETAILS),
      onClick: () => handleShowItemDialog(editItems),
      enabled: editItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DELETE, editItems.length),
      onClick: () => handleDelete(editItems),
      enabled: editItems.length > 0
    }
  ]

  return <SplitButtonDropdown actions={actions} maxButtons={1} />
}

export const TradingEntityRelationshipsPanelActionsButton = withModal(
  connect(mapStateToProps, mapDispatchToProps)(TradingEntityRelationshipsActionsButtonComponent)
)
